/**
 * Orders — live order management with filters, pagination, detail,
 * status enquiry and manual resolution.
 */
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PageHead } from "../components/PageHead";
import { EmptyState, ErrorCard, LoadingCard } from "../components/Async";
import {
  useAdminQuery,
  useDebouncedValue,
  downloadCsv,
  shortDateTime,
  errorMessage,
  type Paged,
} from "../lib/admin";
import { adminApi } from "../lib/api";
import type { AdminOrder, OrderDetail } from "../lib/types";
import {
  Search,
  Download,
  ArrowRight,
  X,
  Clock,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const statusBadge: Record<string, string> = {
  SUCCESS: "ev-badge-mint",
  FAILED: "ev-badge-red",
  PENDING: "ev-badge-amber",
  MANUAL_REVIEW: "ev-badge-amber",
  CANCELLED: "ev-badge-blue",
};

const statusIcon: Record<string, React.ReactNode> = {
  SUCCESS: <CheckCircle2 size={14} />,
  FAILED: <XCircle size={14} />,
  PENDING: <Clock size={14} />,
  MANUAL_REVIEW: <AlertTriangle size={14} />,
  CANCELLED: <XCircle size={14} />,
};

const NETWORKS = ["VODACOM", "AIRTEL", "TIGO", "HALOTEL"];

export function Orders() {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const selectedRef = params.get("ref");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [network, setNetwork] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [evidence, setEvidence] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);
  const pageSize = 25;

  const reference = useDebouncedValue(search);
  const list = useAdminQuery<Paged<AdminOrder>>(
    ["orders", reference, statusFilter, network, fromDate, toDate, page],
    "/admin/v1/orders",
    {
      reference: reference || undefined,
      status: statusFilter === "all" ? undefined : statusFilter,
      network: network === "all" ? undefined : network,
      from_date: fromDate || undefined,
      to_date: toDate || undefined,
      page,
      page_size: pageSize,
    },
  );

  const detail = useAdminQuery<OrderDetail>(
    ["order", selectedRef],
    `/admin/v1/orders/${selectedRef}`,
    {},
    Boolean(selectedRef),
  );

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["admin"] });
    setActionError(null);
  };

  const enquiry = useMutation({
    mutationFn: (ref: string) => adminApi.post(`/admin/v1/orders/${ref}/status-enquiry`),
    onSuccess: invalidate,
    onError: (error) => setActionError(errorMessage(error)),
  });

  const resolve = useMutation({
    mutationFn: (input: { ref: string; outcome: "SUCCESS" | "FAILED"; evidence: string }) =>
      adminApi.post(`/admin/v1/orders/${input.ref}/resolve`, undefined, {
        query: { outcome: input.outcome, evidence: input.evidence },
      }),
    onSuccess: () => { invalidate(); setEvidence(""); },
    onError: (error) => setActionError(errorMessage(error)),
  });

  const rows = list.data?.data ?? [];
  const total = list.data?.total ?? rows.length;
  const closeDrawer = () => {
    params.delete("ref");
    setParams(params, { replace: true });
    setEvidence("");
    setActionError(null);
  };

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setNetwork("all");
    setFromDate("");
    setToDate("");
    setPage(1);
  };

  const exportCsv = () => {
    downloadCsv(
      "evocha-orders.csv",
      ["Reference", "Agent", "Recipient", "Network", "Amount", "Commission", "Status", "Provider", "Created"],
      rows.map((o) => [
        o.ref,
        o.agent?.business_name ?? o.agent_id,
        o.msisdn_masked,
        o.mno,
        o.amount_minor / 100,
        o.commission_minor / 100,
        o.status_label,
        o.provider,
        o.created_at ?? "",
      ]),
    );
  };

  return (
    <>
      <PageHead
        title="Orders"
        description="Airtime order management, tracking, and resolution."
        actions={
          <button className="ev-btn ev-btn-sm ev-btn-outline" onClick={exportCsv} disabled={rows.length === 0}>
            <Download size={16} /> Export
          </button>
        }
      />

      <div className="admin-card-header admin-filter-bar" style={{ marginBottom: 16 }}>
        <div className="admin-filters" style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ position: "relative", flex: 1, minWidth: 220 }}>
            <Search size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--ev-slate-400)", pointerEvents: "none" }} />
            <input
              className="ev-input"
              placeholder="Search reference…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              style={{ paddingLeft: 36 }}
            />
          </div>
          <select className="ev-select" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <option value="all">All status</option>
            <option value="SUCCESS">Success</option>
            <option value="PENDING">Pending</option>
            <option value="MANUAL_REVIEW">Under review</option>
            <option value="FAILED">Failed</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
          <select className="ev-select" value={network} onChange={(e) => { setNetwork(e.target.value); setPage(1); }}>
            <option value="all">All networks</option>
            {NETWORKS.map((n) => (
              <option key={n} value={n}>{n.charAt(0) + n.slice(1).toLowerCase()}</option>
            ))}
          </select>
          <input className="ev-input" type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setPage(1); }} aria-label="From date" />
          <input className="ev-input" type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setPage(1); }} aria-label="To date" />
          <button className="ev-btn ev-btn-ghost ev-btn-sm" onClick={clearFilters}>
            <X size={14} /> Clear
          </button>
        </div>
      </div>

      {actionError && (
        <div className="admin-note" style={{ marginBottom: 12 }}>
          <AlertTriangle size={16} />
          <span>{actionError}</span>
        </div>
      )}

      {list.isLoading && <LoadingCard label="Loading orders…" />}
      {list.error && <ErrorCard error={list.error} onRetry={() => void list.refetch()} />}
      {list.data && rows.length === 0 && <EmptyState>No orders match these filters.</EmptyState>}

      {rows.length > 0 && (
        <>
          <div className="admin-card admin-stat-bar" style={{ marginBottom: 16, padding: 16, display: "flex", gap: 32, flexWrap: "wrap" }}>
            <div className="admin-stat-row">
              <span className="ev-body-muted">Total orders</span>
              <strong>{total}</strong>
            </div>
            <div className="admin-stat-row">
              <span className="ev-body-muted" style={{ color: "var(--ev-mint-600)" }}>Success</span>
              <strong style={{ color: "var(--ev-mint-600)" }}>{rows.filter((o) => o.status === "SUCCESS").length}</strong>
            </div>
            <div className="admin-stat-row">
              <span className="ev-body-muted" style={{ color: "var(--ev-amber-600)" }}>Pending</span>
              <strong style={{ color: "var(--ev-amber-600)" }}>{rows.filter((o) => o.status === "PENDING").length}</strong>
            </div>
            <div className="admin-stat-row">
              <span className="ev-body-muted" style={{ color: "var(--ev-amber-600)" }}>Under review</span>
              <strong style={{ color: "var(--ev-amber-600)" }}>{rows.filter((o) => o.status === "MANUAL_REVIEW").length}</strong>
            </div>
            <div className="admin-stat-row">
              <span className="ev-body-muted" style={{ color: "var(--ev-red-600)" }}>Failed</span>
              <strong style={{ color: "var(--ev-red-600)" }}>{rows.filter((o) => o.status === "FAILED").length}</strong>
            </div>
            <span className="ev-body-micro muted">Counts reflect this page</span>
          </div>

          <div className="admin-card" style={{ padding: 0, overflow: "hidden" }}>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th className="admin-table-th">Reference</th>
                    <th className="admin-table-th">Agent</th>
                    <th className="admin-table-th">Recipient</th>
                    <th className="admin-table-th">Network</th>
                    <th className="admin-table-th">Amount</th>
                    <th className="admin-table-th">Provider</th>
                    <th className="admin-table-th">Status</th>
                    <th className="admin-table-th">Time</th>
                    <th className="admin-table-th" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((o) => (
                    <tr
                      key={o.ref}
                      className="admin-table-row-hover"
                      style={{ cursor: "pointer" }}
                      onClick={() => setParams({ ref: o.ref })}
                    >
                      <td className="admin-table-td" data-label="Reference"><span className="ev-body-mono ev-body-semibold">{o.ref}</span></td>
                      <td className="admin-table-td" data-label="Agent">{o.agent?.business_name ?? o.agent_id.slice(0, 10)}</td>
                      <td className="admin-table-td" data-label="Recipient"><span className="ev-body-mono">{o.msisdn_masked}</span></td>
                      <td className="admin-table-td" data-label="Network">{o.mno}</td>
                      <td className="admin-table-td" data-label="Amount"><span className="ev-body-mono ev-body-semibold">{o.amount_display}</span></td>
                      <td className="admin-table-td" data-label="Provider"><span className="ev-body-micro">{o.provider}</span></td>
                      <td className="admin-table-td" data-label="Status">
                        <span className={`ev-badge ${statusBadge[o.status] ?? "ev-badge-blue"}`}>
                          {statusIcon[o.status]}
                          {o.status_label}
                        </span>
                      </td>
                      <td className="admin-table-td" data-label="Time"><span className="ev-body-micro muted">{shortDateTime(o.created_at)}</span></td>
                      <td className="admin-table-td" data-label=""><ArrowRight size={16} style={{ color: "var(--ev-slate-400)" }} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {total > pageSize && (
            <div className="admin-pagination">
              <button className="ev-btn ev-btn-sm ev-btn-ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                <ChevronLeft size={16} /> Previous
              </button>
              <span className="ev-body-micro">Page {page} · {total} orders</span>
              <button className="ev-btn ev-btn-sm ev-btn-ghost" disabled={page * pageSize >= total} onClick={() => setPage((p) => p + 1)}>
                Next <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}

      {selectedRef && (
        <div className="admin-drawer-overlay" onClick={closeDrawer}>
          <div className="admin-drawer" onClick={(e) => e.stopPropagation()} style={{ width: 520, maxWidth: "100%" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <h3 style={{ marginBottom: 4 }}>{selectedRef}</h3>
                {detail.data && (
                  <span className={`ev-badge ${statusBadge[detail.data.status] ?? "ev-badge-blue"}`}>
                    {detail.data.status_label}
                  </span>
                )}
              </div>
              <button className="ev-icon-btn" onClick={closeDrawer} aria-label="Close">
                <X size={20} />
              </button>
            </div>

            {detail.isLoading && <div style={{ marginTop: 24 }}><LoadingCard label="Loading order…" /></div>}
            {detail.error && <div style={{ marginTop: 24 }}><ErrorCard error={detail.error} /></div>}

            {detail.data && (
              <>
                <div style={{ marginTop: 24 }}>
                  <div className="admin-detail-grid">
                    <div>
                      <div className="ev-body-micro muted">Agent</div>
                      <div className="ev-body-semibold">{detail.data.agent_detail?.business_name ?? detail.data.agent_id}</div>
                    </div>
                    <div>
                      <div className="ev-body-micro muted">Recipient</div>
                      <div className="ev-body-mono">{detail.data.msisdn_masked}</div>
                    </div>
                    <div>
                      <div className="ev-body-micro muted">Network</div>
                      <div className="ev-body">{detail.data.mno}</div>
                    </div>
                    <div>
                      <div className="ev-body-micro muted">Amount</div>
                      <div className="ev-body-semibold">{detail.data.amount_display}</div>
                    </div>
                    <div>
                      <div className="ev-body-micro muted">Commission</div>
                      <div className="ev-body">{detail.data.commission_minor / 100} TZS</div>
                    </div>
                    <div>
                      <div className="ev-body-micro muted">Provider</div>
                      <div className="ev-body">{detail.data.provider}</div>
                    </div>
                    <div>
                      <div className="ev-body-micro muted">Channel</div>
                      <div className="ev-body">{detail.data.channel}</div>
                    </div>
                    <div>
                      <div className="ev-body-micro muted">Created</div>
                      <div className="ev-body">{shortDateTime(detail.data.created_at)}</div>
                    </div>
                  </div>
                  {detail.data.failure_reason && (
                    <div className="admin-note" style={{ marginTop: 16 }}>
                      <AlertTriangle size={16} />
                      <span>{detail.data.failure_reason}</span>
                    </div>
                  )}
                </div>

                {(detail.data.status === "PENDING" || detail.data.status === "MANUAL_REVIEW") && (
                  <div style={{ marginTop: 24, borderTop: "1px solid var(--ev-border)", paddingTop: 16 }}>
                    <h4>Resolution</h4>
                    <p className="ev-body-muted" style={{ marginTop: 4 }}>
                      Reconcile with the provider, then settle the order. This posts the matching ledger journal.
                    </p>
                    <input
                      className="ev-input"
                      placeholder="Evidence / provider reference"
                      value={evidence}
                      onChange={(e) => setEvidence(e.target.value)}
                      style={{ width: "100%", marginTop: 12 }}
                    />
                    <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                      <button
                        className="ev-btn ev-btn-sm ev-btn-outline"
                        disabled={enquiry.isPending}
                        onClick={() => enquiry.mutate(detail.data!.ref)}
                      >
                        <RotateCcw size={14} /> Status enquiry
                      </button>
                      <button
                        className="ev-btn ev-btn-sm ev-btn-primary"
                        disabled={resolve.isPending}
                        onClick={() => resolve.mutate({ ref: detail.data!.ref, outcome: "SUCCESS", evidence })}
                      >
                        <CheckCircle2 size={14} /> Mark success
                      </button>
                      <button
                        className="ev-btn ev-btn-sm ev-btn-outline"
                        disabled={resolve.isPending}
                        onClick={() => resolve.mutate({ ref: detail.data!.ref, outcome: "FAILED", evidence })}
                      >
                        <XCircle size={14} /> Mark failed
                      </button>
                    </div>
                  </div>
                )}

                <h4 style={{ marginTop: 24, marginBottom: 8 }}>Provider attempts</h4>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {detail.data.attempts.length === 0 && (
                    <span className="ev-body-micro muted">No provider attempts recorded.</span>
                  )}
                  {detail.data.attempts.map((attempt) => (
                    <div key={attempt.id}>
                      <div className="ev-body-micro">
                        {attempt.provider} · {attempt.status} · {shortDateTime(attempt.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
